import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCheckDouble } from "@fortawesome/free-solid-svg-icons/faCheckDouble";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons/faCircleExclamation";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faForward } from "@fortawesome/free-solid-svg-icons/faForward";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons/faPaperPlane";
import { faPause } from "@fortawesome/free-solid-svg-icons/faPause";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import dayjs from "dayjs";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import { IntegrationAxios } from "../../../../axios/integration/integration.axios";
import { AtiraEmpty } from "../../../../components/AtiraEmpty";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { Drawer } from "../../../../components/Drawer";
import { Flex } from "../../../../components/Flex";
import { Spinner } from "../../../../components/Spinner";
import { Text } from "../../../../components/Text";
import { WarningModal } from "../../../../components/WarningModal";
import { WhatsappCampaignDetailsResponseDto } from "../../../../model/integration/dto/whatsapp-campaign/WhatsappCampaignDetailsResponseDto";
import { WhatsappCampaignResponseDto } from "../../../../model/integration/dto/whatsapp-campaign/WhatsappCampaignResponseDto";
import { WhatsappCampaignRecipientStatus } from "../../../../model/integration/types/whatsapp/WhatsappCampaignRecipientStatus.enum";
import { WhatsappCampaignStatus } from "../../../../model/integration/types/whatsapp/WhatsappCampaignStatus.enum";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { ThemeType } from "../../../../theme/Theme";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Progress } from "../../../../utils/local-antd";
import { TestIDs } from "../../../../tests/types/testIds";
import { CURRENT_CAMPAIGN_STATUSES } from "./utils/campaignStatus";
import { formatCampaignTime } from "./utils/campaignTime";
import { WACampaignCreateEditDrawer } from "./WACampaignCreateEditDrawer";

const POLL_MS = 15_000;

const Card = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.md};
`;

const Chip = styled.span<{ $color: keyof ThemeType }>`
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.pill};
  font-size: 0.75rem;
  white-space: nowrap;
  color: ${({ theme, $color }) => theme[$color]};
  border: 1px solid ${({ theme, $color }) => theme[$color]};
`;

const ActionButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs} ${Spacing.s};
`;

const RecipientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: ${Spacing.s};
  max-height: 22rem;
  overflow-y: auto;
  padding-inline-end: ${Spacing.xs};
`;

const RecipientCard = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xs};
  min-width: 0;
  padding: ${Spacing.s};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.md};
`;

const STATS: {
  key: "sent" | "delivered" | "read" | "failed" | "skipped";
  icon: IconDefinition;
  color: keyof ThemeType;
}[] = [
  { key: "sent", icon: faPaperPlane, color: "main" },
  { key: "delivered", icon: faCheckDouble, color: "success" },
  { key: "read", icon: faEye, color: "lightAccent" },
  { key: "failed", icon: faCircleExclamation, color: "danger" },
  { key: "skipped", icon: faForward, color: "warning" },
];

type Phase = "scheduled" | "sending" | "paused" | "completed" | "deleted";

const PHASE_COLOR: Record<Phase, keyof ThemeType> = {
  scheduled: "darkerSub",
  sending: "main",
  paused: "warning",
  completed: "success",
  deleted: "darkerSub",
};

const phaseOf = (campaign: WhatsappCampaignResponseDto): Phase => {
  switch (campaign.status) {
    case WhatsappCampaignStatus.ACTIVE:
      return dayjs(campaign.startAt).isAfter(dayjs()) &&
        !campaign.counts.sent &&
        !campaign.counts.failed
        ? "scheduled"
        : "sending";
    case WhatsappCampaignStatus.PAUSED:
      return "paused";
    case WhatsappCampaignStatus.COMPLETED:
      return "completed";
    default:
      return "deleted";
  }
};

const RECIPIENT_COLOR: Record<string, keyof ThemeType> = {
  [WhatsappCampaignRecipientStatus.FAILED]: "danger",
  [WhatsappCampaignRecipientStatus.SKIPPED]: "warning",
  [WhatsappCampaignRecipientStatus.SENT]: "success",
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export const WACampaignsDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WhatsappCampaignDetailsResponseDto>();
  const [expandedId, setExpandedId] = useState<string>();
  const [details, setDetails] = useState<WhatsappCampaignDetailsResponseDto>();
  // Read by the poll and by answers arriving late, which must not act on a
  // card that has since closed or been swapped for another
  const expandedRef = useRef<string | undefined>(undefined);
  const [busyId, setBusyId] = useState<string>();
  const [deleting, setDeleting] = useState<WhatsappCampaignResponseDto>();

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const timezone = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.settings?.timezone;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const campaigns = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaCampaigns,
  );
  const loaded = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaCampaignsLoaded,
  );

  const integrationId = integration?._id;

  const current = campaigns.filter(({ status }) =>
    CURRENT_CAMPAIGN_STATUSES.includes(status),
  );
  const previous = campaigns.filter(
    ({ status }) => !CURRENT_CAMPAIGN_STATUSES.includes(status),
  );

  const loadDetails = useCallback(
    async (campaignId: string) => {
      const found = await IntegrationAxios.getWhatsappMetaCampaign({
        userId,
        integrationId: integrationId!,
        campaignId,
      });

      if (expandedRef.current === campaignId) {
        setDetails(found);
      }
    },
    [userId, integrationId],
  );

  const refresh = useCallback(() => {
    if (!integrationId) {
      return;
    }

    dispatch(
      integrationActions.getWhatsappMetaCampaigns({ userId, integrationId }),
    );

    if (expandedRef.current) {
      loadDetails(expandedRef.current).catch(() => undefined);
    }
  }, [dispatch, userId, integrationId, loadDetails]);

  const expand = (campaignId?: string) => {
    expandedRef.current = campaignId;
    setExpandedId(campaignId);
  };

  const anyRunning = campaigns.some(
    ({ status }) => status === WhatsappCampaignStatus.ACTIVE,
  );

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open, refresh]);

  useEffect(() => {
    if (!open || !anyRunning) {
      return;
    }

    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [open, anyRunning, refresh]);

  const run = async (
    campaign: WhatsappCampaignResponseDto,
    action:
      | "pauseWhatsappMetaCampaign"
      | "resumeWhatsappMetaCampaign"
      | "deleteWhatsappMetaCampaign",
  ) => {
    try {
      setBusyId(campaign._id);

      await dispatch(
        integrationActions[action]({
          userId,
          integrationId: integrationId!,
          campaignId: campaign._id,
        }),
      ).unwrap();

      if (
        action === "deleteWhatsappMetaCampaign" &&
        expandedId === campaign._id
      ) {
        expand(undefined);
      }

      refresh();
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setBusyId(undefined);
    }
  };

  const onEdit = async (campaign: WhatsappCampaignResponseDto) => {
    try {
      setBusyId(campaign._id);
      // Not through the store: that slot belongs to whichever campaign has its
      // recipients open
      setEditing(
        await IntegrationAxios.getWhatsappMetaCampaign({
          userId,
          integrationId: integrationId!,
          campaignId: campaign._id,
        }),
      );
      setEditorOpen(true);
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setBusyId(undefined);
    }
  };

  const onToggleDetails = async (campaign: WhatsappCampaignResponseDto) => {
    if (expandedId === campaign._id) {
      expand(undefined);
      return;
    }

    expand(campaign._id);

    try {
      await loadDetails(campaign._id);
    } catch (e) {
      if (expandedRef.current === campaign._id) {
        expand(undefined);
      }
      AtiraToast.apiError(e);
    }
  };

  const statusLine = (campaign: WhatsappCampaignResponseDto) => {
    const phase = phaseOf(campaign);
    const at = (date?: string) => formatCampaignTime(date, timezone);

    switch (phase) {
      case "scheduled":
        return t("integration.whatsapp.meta.campaign.line.scheduled", {
          date: at(campaign.startAt),
        });
      case "sending":
        return t("integration.whatsapp.meta.campaign.line.sending", {
          date: at(campaign.nextSendAt),
        });
      case "paused":
        return t(
          `integration.whatsapp.meta.campaign.pauseReason.${campaign.pauseReason}`,
        );
      case "completed":
        return t("integration.whatsapp.meta.campaign.line.completed", {
          date: at(campaign.completedAt),
        });
      case "deleted":
        return t("integration.whatsapp.meta.campaign.line.deleted", {
          date: at(campaign.deletedAt),
        });
    }
  };

  const recipients =
    details && details._id === expandedId ? details.recipients : undefined;

  return (
    <Drawer
      title={t("integration.whatsapp.meta.campaign.title")}
      open={open}
      onClose={onClose}
      size="large"
      extra={
        <Button
          icon={faPlus}
          dataTestId={TestIDs.integration.whatsappMeta.campaign.new}
          disabled={current.length > 0}
          info={
            current.length
              ? t("integration.whatsapp.meta.campaign.oneAtATime")
              : undefined
          }
          onClick={() => {
            setEditing(undefined);
            setEditorOpen(true);
          }}
        >
          {t("integration.whatsapp.meta.campaign.new")}
        </Button>
      }
    >
      <Flex flexDirection="column" gap="m" width="100%">
        {loaded && !campaigns.length ? (
          <AtiraEmpty
            description={t("integration.whatsapp.meta.campaign.empty")}
          />
        ) : null}

        {[...current, ...previous].map((campaign) => {
          const phase = phaseOf(campaign);
          const { counts } = campaign;
          const done = counts.sent + counts.failed + counts.skipped;
          const retired = !CURRENT_CAMPAIGN_STATUSES.includes(campaign.status);

          return (
            <Fragment key={campaign._id}>
              {campaign._id === previous[0]?._id ? (
                <Text fontWeight="bold" style={{ marginTop: Spacing.m }}>
                  {t("integration.whatsapp.meta.campaign.previous")}
                </Text>
              ) : null}

              <Card dataTestId={TestIDs.integration.whatsappMeta.campaign.card}>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  gap="s"
                  flexWrap="wrap"
                >
                  <Flex flexDirection="column" style={{ minWidth: 0 }}>
                    <Text fontWeight="bold" wordBreak="break-word">
                      {campaign.name}
                    </Text>
                    <Text fontSize="xs" color="darkerSub">
                      {campaign.template.name}
                    </Text>
                  </Flex>

                  <Chip $color={PHASE_COLOR[phase]}>
                    {t(`integration.whatsapp.meta.campaign.phase.${phase}`)}
                  </Chip>
                </Flex>

                <Progress
                  percent={
                    counts.total ? Math.round((done / counts.total) * 100) : 0
                  }
                  showInfo={false}
                  size="small"
                  strokeColor={
                    phase === "completed" ? theme.success : theme.main
                  }
                />

                <Flex gap="m" flexWrap="wrap">
                  {STATS.map(({ key, icon, color }) => {
                    const count = counts[key];
                    const tint =
                      !count && (key === "failed" || key === "skipped")
                        ? "darkerSub"
                        : color;

                    return (
                      <Flex key={key} alignItems="center" gap="xs">
                        <AtiraIcon icon={icon} color={tint} size="xs" />
                        <Text fontSize="s" color={tint}>
                          {t(`integration.whatsapp.meta.campaign.stat.${key}`, {
                            count,
                            total: counts.total,
                          })}
                        </Text>
                      </Flex>
                    );
                  })}
                </Flex>

                {statusLine(campaign) ? (
                  <Text
                    fontSize="s"
                    color={phase === "paused" ? "warning" : "darkerSub"}
                  >
                    {statusLine(campaign)}
                  </Text>
                ) : null}

                <Flex gap="s" flexWrap="wrap">
                  {campaign.status === WhatsappCampaignStatus.ACTIVE ? (
                    <ActionButton
                      icon={faPause}
                      loading={busyId === campaign._id}
                      onClick={() => run(campaign, "pauseWhatsappMetaCampaign")}
                    >
                      {t("integration.whatsapp.meta.campaign.pause")}
                    </ActionButton>
                  ) : null}

                  {campaign.status === WhatsappCampaignStatus.PAUSED ? (
                    <ActionButton
                      icon={faPlay}
                      loading={busyId === campaign._id}
                      onClick={() =>
                        run(campaign, "resumeWhatsappMetaCampaign")
                      }
                    >
                      {t("integration.whatsapp.meta.campaign.resume")}
                    </ActionButton>
                  ) : null}

                  {!retired ? (
                    <ActionButton
                      icon={faPen}
                      dataTestId={
                        TestIDs.integration.whatsappMeta.campaign.edit
                      }
                      disabled={busyId === campaign._id}
                      onClick={() => onEdit(campaign)}
                    >
                      {t("common.edit")}
                    </ActionButton>
                  ) : null}

                  {campaign.status === WhatsappCampaignStatus.PAUSED ? (
                    <ActionButton
                      icon={faTrash}
                      backgroundColor="danger"
                      disabled={busyId === campaign._id}
                      onClick={() => setDeleting(campaign)}
                    >
                      {t("common.delete")}
                    </ActionButton>
                  ) : null}

                  <ActionButton
                    backgroundColor="darkerSub"
                    icon={
                      expandedId === campaign._id ? faChevronUp : faChevronDown
                    }
                    onClick={() => onToggleDetails(campaign)}
                  >
                    {t("integration.whatsapp.meta.campaign.details")}
                  </ActionButton>
                </Flex>

                {expandedId === campaign._id && !recipients ? (
                  <Flex justifyContent="center" padding="s">
                    <Spinner color="main" size="1.5rem" />
                  </Flex>
                ) : null}

                {expandedId === campaign._id && recipients ? (
                  <RecipientGrid>
                    {recipients.map((recipient) => (
                      <RecipientCard key={recipient._id}>
                        <Text
                          fontSize="s"
                          fontWeight="bold"
                          wordBreak="break-word"
                        >
                          {recipient.name || `+${recipient.waId}`}
                        </Text>

                        {recipient.name ? (
                          <Text fontSize="xs" color="darkerSub" dir="ltr">
                            {`+${recipient.waId}`}
                          </Text>
                        ) : null}

                        <Text
                          fontSize="xs"
                          color={
                            RECIPIENT_COLOR[recipient.status] || "darkerSub"
                          }
                        >
                          {recipient.status ===
                            WhatsappCampaignRecipientStatus.SENT &&
                          recipient.messageStatus
                            ? t(
                                `integration.whatsapp.meta.campaign.recipient.message.${recipient.messageStatus}`,
                                {
                                  defaultValue: t(
                                    "integration.whatsapp.meta.campaign.recipient.message.sent",
                                  ),
                                },
                              )
                            : recipient.skipReason
                              ? t(
                                  `integration.whatsapp.meta.campaign.recipient.skip.${recipient.skipReason}`,
                                )
                              : retired &&
                                  recipient.status ===
                                    WhatsappCampaignRecipientStatus.PENDING
                                ? t(
                                    "integration.whatsapp.meta.campaign.recipient.notSent",
                                  )
                                : t(
                                    `integration.whatsapp.meta.campaign.recipient.status.${recipient.status}`,
                                  )}
                        </Text>

                        {recipient.error ? (
                          <Text
                            fontSize="xs"
                            color="darkerSub"
                            wordBreak="break-word"
                          >
                            {recipient.error}
                          </Text>
                        ) : null}
                      </RecipientCard>
                    ))}
                  </RecipientGrid>
                ) : null}
              </Card>
            </Fragment>
          );
        })}
      </Flex>

      <WACampaignCreateEditDrawer
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        campaign={editing}
        onSaved={refresh}
      />

      <WarningModal
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(undefined)}
        title={t("integration.whatsapp.meta.campaign.deleteModal.title")}
        description={t(
          "integration.whatsapp.meta.campaign.deleteModal.description",
        )}
        loading={busyId === deleting?._id}
        onConfirm={async () => {
          if (deleting) {
            await run(deleting, "deleteWhatsappMetaCampaign");
          }
          setDeleting(undefined);
        }}
      />
    </Drawer>
  );
};
